import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  BookMeetingService,
  RoomSelection,
} from '../../../../../core/services/book-meeting.service';
import { GraphQLClientService } from '../../../../../core/services/graphql-client.service';
import { LocaleService } from '../../../../../core/services/locale.service';
import { RealtimeService } from '../../../../../core/services/realtime.service';
import { SearchService } from '../../../../../core/services/search.service';
import { LocationStateService } from '../../../../../core/services/location-state.service';
import {
  computeRoomPickerResults,
  RoomPickerOption,
  RoomSearchableSpace,
  RoomSearchAvailabilityLookup,
} from './room-picker-search';

type RoomOption = RoomPickerOption;

/**
 * RoomPickerInlineComponent — self-contained room picker for the Book Meeting
 * panel.
 *
 * DESIGN GOAL: the Book Meeting panel can be opened from any screen (map,
 * schedule, space-detail, command-K, home widget), so this picker must NOT
 * assume that any upstream page has pre-loaded PlatformDataService. It owns
 * its own fetch of rooms + availability for the primary room's location and
 * subscribes to RealtimeService so availability stays live.
 *
 * LOADING:
 *   - On construction, fires `pickerRooms` + `pickerAvailability` queries
 *     scoped to the primary room's location. Results land in local signals.
 *   - While loading, the input is shown and a "Loading rooms…" hint is
 *     rendered in place of the results list.
 *
 * SEARCH (pure logic in room-picker-search.ts, unit-tested):
 *   - Always local substring match (case-insensitive, prefix-first) against
 *     our locally-loaded rooms.
 *   - For queries >= 4 chars, also calls SearchService.searchMap (300 ms
 *     debounced, already in the codebase) and merges any extra meetingroom
 *     hits on top — so a name synonym / amenity match the backend knows
 *     about still surfaces even if our substring filter missed it.
 *
 * AVAILABILITY:
 *   - Read from the local `_availabilityMap` signal — set by the initial
 *     fetch and refreshed when RealtimeService.lastBookingChange fires.
 *   - Busy rooms are disabled in the picker and carry an "In use" pill.
 */
@Component({
  selector: 'im-room-picker-inline',
  standalone: true,
  imports: [FormsModule, TranslocoPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (readonly()) {
      <!-- Picker is suppressed in read-only mode. Defensive: the panel's
           "Add another room" CTA is hidden so this should never mount,
           but if it does we render nothing rather than a search input. -->
    } @else {
    <div class="rpi" data-testid="room-picker-inline">
      <input
        class="rpi__input"
        type="text"
        [placeholder]="'viewer.book_meeting_panel.room_picker.search_placeholder' | transloco"
        [attr.aria-label]="'viewer.book_meeting_panel.room_picker.search_placeholder' | transloco"
        [(ngModel)]="query"
        (ngModelChange)="onQueryChanged($event)"
        data-testid="room-picker-input"
      />
      @if (dataStatus() === 'loading' && rooms().length === 0) {
        <p class="rpi__hint">{{ 'viewer.book_meeting_panel.room_picker.loading_rooms' | transloco }}</p>
      } @else if (dataStatus() === 'error') {
        <p class="rpi__hint rpi__hint--error">
          {{ 'viewer.book_meeting_panel.room_picker.load_error' | transloco }}
          <button class="rpi__retry" type="button" (click)="reload()">{{ 'viewer.book_meeting_panel.room_picker.try_again' | transloco }}</button>
        </p>
      } @else if (backendLoading()) {
        <p class="rpi__hint">{{ 'viewer.book_meeting_panel.room_picker.searching' | transloco }}</p>
      } @else if (query().trim().length >= 1 && results().length === 0) {
        <p class="rpi__hint">
          {{ 'viewer.book_meeting_panel.room_picker.no_rooms_match' | transloco: { query: query() } }}
          @if (rooms().length > 0) {
            {{ 'viewer.book_meeting_panel.room_picker.n_rooms_available' | transloco: { count: rooms().length } }}
          }
        </p>
      } @else if (results().length > 0) {
        <ul class="rpi__list">
          @for (room of results(); track room._id) {
            <li>
              <button
                class="rpi__item"
                (click)="selectRoom(room)"
                [disabled]="alreadySelected(room._id) || room.availability === 'busy'"
                [attr.data-testid]="'room-option-' + room._id"
                [attr.data-availability]="room.availability">
                <span class="rpi__item-name">{{ room.name }}</span>
                <span
                  class="rpi__item-status"
                  [class.rpi__item-status--available]="room.availability === 'available'"
                  [class.rpi__item-status--busy]="room.availability === 'busy'">
                  {{ room.availabilityLabel }}
                </span>
                @if (alreadySelected(room._id)) {
                  <span class="rpi__item-selected">{{ 'viewer.book_meeting_panel.room_picker.added' | transloco }}</span>
                }
              </button>
            </li>
          }
        </ul>
      }
      <button class="rpi__cancel" (click)="cancel.emit()">{{ 'viewer.book_meeting_panel.room_picker.cancel' | transloco }}</button>
    </div>
    }
  `,
  styles: [`
    .rpi {
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding: 12px;
      background: var(--surface-2);
      border-radius: var(--radius-sm);
      border: 1px solid var(--border-low);
    }

    .rpi__input {
      width: 100%;
      padding: 8px 10px;
      font-size: 12px;
      border: 1px solid var(--border-mid);
      border-radius: var(--radius-sm);
      background: var(--surface-1);
      color: var(--text);
    }

    .rpi__hint {
      margin: 0;
      font-size: 11px;
      color: var(--text-tertiary);
    }

    .rpi__hint--error {
      color: var(--danger);
    }

    .rpi__retry {
      margin-left: 6px;
      padding: 2px 8px;
      font-size: 11px;
      color: var(--color-primary);
      background: transparent;
      border: 1px solid var(--color-primary);
      border-radius: var(--radius-xs);
      cursor: pointer;
    }

    .rpi__list {
      list-style: none;
      margin: 0;
      padding: 0;
      max-height: 200px;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .rpi__item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      padding: 8px 10px;
      font-size: 12px;
      color: var(--text);
      background: transparent;
      border: none;
      border-radius: var(--radius-xs);
      cursor: pointer;
      text-align: left;
      transition: background 0.12s ease;
    }

    .rpi__item:hover:not(:disabled) {
      background: var(--surface-3);
    }

    .rpi__item:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }

    .rpi__item-selected {
      font-size: 10px;
      color: var(--text-tertiary);
    }

    .rpi__item-status {
      margin-left: auto;
      margin-right: 6px;
      font-size: 10px;
      font-weight: 600;
      padding: 2px 6px;
      border-radius: var(--radius-pill);
      color: var(--text-tertiary);
      background: var(--surface-3);
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }

    .rpi__item-status--available {
      color: var(--success);
      background: color-mix(in srgb, var(--success) 16%, transparent);
    }

    .rpi__item-status--busy {
      color: var(--danger);
      background: color-mix(in srgb, var(--danger) 14%, transparent);
    }

    .rpi__cancel {
      align-self: flex-start;
      padding: 4px 8px;
      font-size: 11px;
      color: var(--text-secondary);
      background: transparent;
      border: none;
      cursor: pointer;
    }

    .rpi__cancel:hover {
      color: var(--text);
    }

    @media (prefers-reduced-motion: reduce) {
      .rpi__item { transition: none; }
    }
  `],
})
export class RoomPickerInlineComponent {
  private readonly service = inject(BookMeetingService);
  private readonly graphql = inject(GraphQLClientService);
  private readonly realtime = inject(RealtimeService);
  private readonly searchService = inject(SearchService);
  private readonly destroyRef = inject(DestroyRef);
  /** Falls through to the active grid location when the panel has no
   *  primary room yet (eg. the user opened the panel from a button,
   *  not a schedule cell). Avoids the "Couldn't load rooms" error
   *  loop when there's no room context to inherit from. */
  private readonly locationState = inject(LocationStateService);

  readonly cancel = output<void>();

  /** Driven by BookMeetingService.mode — when 'view' the picker renders
   *  nothing. Belt-and-braces: the parent panel hides the CTA that
   *  surfaces this component, but if it's mounted anyway we still
   *  refuse to render. */
  protected readonly readonly = computed(() => this.service.mode() === 'view');

  protected readonly query = signal('');

  // ── Own data layer ──────────────────────────────────────────────────────

  private readonly _rooms = signal<RoomSearchableSpace[]>([]);
  private readonly _availability = signal<
    ReadonlyMap<string, RoomSearchAvailabilityLookup>
  >(new Map());
  private readonly _dataStatus = signal<
    'idle' | 'loading' | 'loaded' | 'error'
  >('idle');

  protected readonly rooms = this._rooms.asReadonly();
  protected readonly dataStatus = this._dataStatus.asReadonly();

  // ── Lifecycle ───────────────────────────────────────────────────────────

  constructor() {
    this.reload();

    // Keep availability fresh on WebSocket booking changes while the picker
    // is open. We refetch the availability slice only (cheap) instead of
    // reloading the full rooms list.
    const seen = { at: 0 };
    effect(() => {
      const change = this.realtime.lastBookingChange();
      if (!change) return;
      // Only react to genuinely new events (the signal fires once on init).
      const ts = Date.now();
      if (ts - seen.at < 200) return;
      seen.at = ts;
      void this.refetchAvailability();
    });
  }

  /** Trigger (or retry) the rooms + availability fetch.
   *
   *  Resolution order for the location:
   *    1. Primary room's locationId — the panel was opened from a
   *       room (schedule cell, calendar click, etc.).
   *    2. LocationStateService.activeLocationId() — the user is on
   *       a location-scoped page; inherit that scope.
   *    3. Idle (no error) — there's truly nothing to search; we
   *       wait for either context to arrive instead of flashing
   *       "Couldn't load rooms" before the user has done anything.
   */
  reload(): void {
    const primary = this.service.primaryRoom();
    const locationId = primary?.locationId
      ?? this.locationState.selectedLocationId()
      ?? null;
    if (!locationId) {
      // No source of location truth yet — stay idle so the picker
      // shows the search input without an error. An effect on
      // primaryRoom + activeLocationId could re-trigger reload when
      // either arrives; for now the user can manually retry.
      this._dataStatus.set('idle');
      return;
    }
    this._dataStatus.set('loading');

    // Timeout: if fetch takes >15s, show error
    const timeout = setTimeout(() => {
      if (this._dataStatus() === 'loading') this._dataStatus.set('error');
    }, 15000);

    void Promise.all([
      this.fetchRooms(locationId),
      this.refetchAvailability(locationId),
    ])
      .then(() => { clearTimeout(timeout); this._dataStatus.set('loaded'); })
      .catch(() => { clearTimeout(timeout); this._dataStatus.set('error'); });
  }

  // ── Data fetches ───────────────────────────────────────────────────────

  private async fetchRooms(locationId: string): Promise<void> {
    const data = await this.graphql.query<{
      _id: string;
      name?: string;
      spaces: Array<{
        _id: string;
        name: string;
        spaceType: string;
        locationId: string;
        floorId: string;
        bookable: boolean;
        image: string | null;
        capacity: number | null;
      }>;
    }>(ROOM_PICKER_ROOMS_QUERY, { locationId });

    if (!data) return;

    const allSpaces = data.spaces ?? [];
    const rooms: RoomSearchableSpace[] = allSpaces
      .filter((s) => s.spaceType?.toLowerCase() === 'meetingroom' && s.bookable !== false)
      .map((s) => ({
        _id: s._id,
        name: s.name,
        spaceType: s.spaceType,
        bookable: s.bookable,
        locationId: s.locationId,
        floorId: s.floorId,
        image: s.image ?? undefined,
        capacity: s.capacity ?? undefined,
      }));

    this._rooms.set(rooms);
  }

  private async refetchAvailability(
    locationIdArg?: string,
  ): Promise<void> {
    const primary = this.service.primaryRoom();
    const locationId = locationIdArg ?? primary?.locationId;
    if (!locationId) return;
    const date = this.pickerDate();
    const data = await this.graphql.query<{
      spaces: Array<{
        spaceId: string;
        hasBooking: boolean;
        availableAtTime: boolean;
      }>;
    }>(ROOM_PICKER_AVAILABILITY_QUERY, { locationId, date }, 'network-only');

    if (!data?.spaces) return;
    const map = new Map<string, RoomSearchAvailabilityLookup>();
    for (const s of data.spaces) {
      map.set(s.spaceId, {
        availableAtTime: s.availableAtTime,
        hasBooking: s.hasBooking,
      });
    }
    this._availability.set(map);
  }

  /** YYYY-MM-DD for the meeting being composed (falls back to today). */
  private pickerDate(): string {
    const start = this.service.startTime();
    const base = start ?? new Date();
    const y = base.getFullYear();
    const m = (base.getMonth() + 1).toString().padStart(2, '0');
    const d = base.getDate().toString().padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  // ── Derived state ──────────────────────────────────────────────────────

  /** True while the server-side searchMap is resolving a debounced query. */
  protected readonly backendLoading = computed(() => {
    if (this.query().trim().length < 4) return false;
    return this.searchService.loading();
  });

  private readonly selectedIds = computed(
    () => new Set(this.service.rooms().map((r) => r.spaceId)),
  );

  protected readonly results = computed<RoomOption[]>(() => {
    const primary = this.service.primaryRoom();
    if (!primary?.locationId) return [];
    const apiHits = this.searchService.results()?.spaces || null;
    const availabilityMap = this._availability();
    return computeRoomPickerResults({
      query: this.query(),
      primaryLocationId: primary.locationId,
      spaces: this._rooms(),
      apiResults: apiHits
        ? (apiHits as Array<{ id: string; name?: string; spaceType?: string }>).map((s) => ({
            id: s.id,
            name: s.name ?? '',
            spaceType: s.spaceType,
          }))
        : null,
      getAvailability: (id) => availabilityMap.get(id),
    });
  });

  // ── Input handling ─────────────────────────────────────────────────────

  protected onQueryChanged(value: string): void {
    const trimmed = value.trim();
    if (trimmed.length < 4) {
      this.searchService.clear();
      return;
    }
    const primary = this.service.primaryRoom();
    if (!primary?.locationId) return;
    // `types` filters the top-level category (space | employee | location |
    // floor), NOT the spaceType subtype — so we pass ['space'] and let the
    // pure search function filter to meetingroom in the merge step.
    this.searchService.searchMap(
      primary.locationId,
      trimmed,
      ['space'],
      20,
      false,
    );
  }

  protected alreadySelected(spaceId: string): boolean {
    return this.selectedIds().has(spaceId);
  }

  protected selectRoom(room: RoomOption): void {
    if (this.alreadySelected(room._id)) return;
    const primary = this.service.primaryRoom();
    if (!primary) return;
    const selection: RoomSelection = {
      spaceId: room._id,
      name: room.name,
      locationId: primary.locationId,
      locationName: primary.locationName,
      building: primary.locationName,
      floorId: room.floorId,
      isPrimary: false,
      image: room.image,
      capacity: room.capacity,
    };
    this.service.addRoom(selection);
    this.query.set('');
    this.searchService.clear();
  }
}

// ── Self-contained GraphQL queries ──────────────────────────────────────
//
// These are intentionally narrow — they fetch only what the picker needs so
// opening the panel from a "cold" route (no map / schedule pre-load) stays
// snappy. They do NOT write into PlatformDataService; the picker owns its
// copy and reacts to WebSocket booking changes on its own.

const ROOM_PICKER_ROOMS_QUERY = `
  query pickerRooms($locationId: ID!) {
    mapLocation(locationId: $locationId) {
      _id
      spaces {
        _id
        name
        spaceType
        locationId
        floorId
        bookable
        image
        capacity
      }
    }
  }
`;

const ROOM_PICKER_AVAILABILITY_QUERY = `
  query pickerAvailability($locationId: ID!, $date: String!) {
    mapAvailability(locationId: $locationId, date: $date) {
      spaces {
        spaceId
        hasBooking
        availableAtTime
      }
    }
  }
`;
